// ─── Security-hardened crypto module (v2) ─────────────────────────────────────
//
// V2 encryption: 3-factor model
//   Factor 1: 128-bit URL key (in URL fragment) — provides 128 bits of entropy
//   Factor 2: Encrypted blob (stored on server)  — requires server access
//   Factor 3: 6-digit PIN (communicated separately) — human-memorable secret
//
// C1: KDF is Argon2id for new blobs (memory-hard → GPU/ASIC-resistant).
// PBKDF2 remains only for backward-compat decryption of pre-C1 blobs.
//
//   new blob: [magic(4)=A2 1D 00 01][salt(16)][iv(12)][ct+tag]
//             AES key = Argon2id(urlKey || pin, salt, m=19MiB, t=2, p=1) → AES-256-GCM
//   legacy:   [salt(16)][iv(12)][ct+tag]   (no magic prefix)
//             AES key = PBKDF2(urlKey || pin, salt, 600000, SHA-256) → AES-256-GCM
//
// Legacy V1 (PIN-only, no urlKey) still decrypts via PBKDF2 for ancient cheques.
// ───────────────────────────────────────────────────────────────────────────────

import { argon2id } from "hash-wasm";

// 4-byte magic distinguishes new Argon2id blobs from legacy PBKDF2 blobs.
// Collision probability with random salt first 4 bytes = 1/2^32 — negligible.
const ARGON2_MAGIC = new Uint8Array([0xA2, 0x1D, 0x00, 0x01]);

// OWASP 2024 interactive parameters. ~0.5s on laptop, ~1-2s on mid-tier phone.
const ARGON2_PARAMS = { parallelism: 1, iterations: 2, memorySize: 19_456 /* KiB */, hashLength: 32 } as const;

function hasArgon2Magic(packed: Uint8Array): boolean {
  return packed.length >= 4
    && packed[0] === ARGON2_MAGIC[0]
    && packed[1] === ARGON2_MAGIC[1]
    && packed[2] === ARGON2_MAGIC[2]
    && packed[3] === ARGON2_MAGIC[3];
}

async function deriveKeyArgon2(secret: string, salt: Uint8Array): Promise<CryptoKey> {
  const raw = await argon2id({
    password: secret,
    salt,
    parallelism: ARGON2_PARAMS.parallelism,
    iterations: ARGON2_PARAMS.iterations,
    memorySize: ARGON2_PARAMS.memorySize,
    hashLength: ARGON2_PARAMS.hashLength,
    outputType: "binary",
  });
  return crypto.subtle.importKey("raw", raw as unknown as ArrayBuffer, { name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
}

// ─── URL key generation ──────────────────────────────────────────────────────

/** Generate a cryptographically random 128-bit key for the URL fragment */
export function generateUrlKey(): string {
  const key = crypto.getRandomValues(new Uint8Array(16));
  return uint8ArrayToBase64Url(key);
}

// ─── Key derivation ──────────────────────────────────────────────────────────

async function deriveKey(secret: string, salt: Uint8Array): Promise<CryptoKey> {
  const enc = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    enc.encode(secret) as unknown as ArrayBuffer,
    "PBKDF2",
    false,
    ["deriveKey"]
  );
  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: salt as unknown as ArrayBuffer,
      iterations: 600000,
      hash: "SHA-256",
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

// ─── Encrypt / Decrypt ───────────────────────────────────────────────────────

/**
 * Encrypt a private key with V2 encryption (urlKey + PIN).
 * V1 (PIN-only) is deprecated and no longer supported for new cheques.
 */
export async function encryptPrivateKey(
  privateKey: string,
  pin: string,
  urlKey: string
): Promise<string> {
  if (!urlKey) throw new Error("urlKey is required — V1 (PIN-only) encryption is deprecated");
  const enc = new TextEncoder();
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const secret = urlKey + pin;
  const key = await deriveKeyArgon2(secret, salt);

  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    enc.encode(privateKey)
  );

  // Pack: magic(4) + salt(16) + iv(12) + ciphertext+tag
  const ct = new Uint8Array(ciphertext);
  const packed = new Uint8Array(4 + 16 + 12 + ct.length);
  packed.set(ARGON2_MAGIC, 0);
  packed.set(salt, 4);
  packed.set(iv, 20);
  packed.set(ct, 32);

  const result = uint8ArrayToBase64Url(packed);

  // Zero sensitive intermediates
  zeroBytes(salt);
  zeroBytes(iv);
  zeroBytes(packed);

  return result;
}

/**
 * Decrypt a private key.
 * V2 (urlKey provided): secret = urlKey + pin
 * V1 (no urlKey):        secret = pin only (legacy — decrypt-only for existing cheques)
 */
export async function decryptPrivateKey(
  encryptedData: string,
  pin: string,
  urlKey?: string
): Promise<string> {
  const packed = base64UrlToUint8Array(encryptedData);
  const secret = urlKey ? urlKey + pin : pin;

  let salt: Uint8Array;
  let iv: Uint8Array;
  let ciphertext: Uint8Array;
  let key: CryptoKey;

  if (hasArgon2Magic(packed)) {
    // New format: magic(4) || salt(16) || iv(12) || ct+tag
    salt = packed.slice(4, 20);
    iv = packed.slice(20, 32);
    ciphertext = packed.slice(32);
    key = await deriveKeyArgon2(secret, salt);
  } else {
    // Legacy PBKDF2 format: salt(16) || iv(12) || ct+tag
    salt = packed.slice(0, 16);
    iv = packed.slice(16, 28);
    ciphertext = packed.slice(28);
    key = await deriveKey(secret, salt);
  }

  const decrypted = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: iv as unknown as ArrayBuffer },
    key,
    ciphertext as unknown as ArrayBuffer
  );

  const result = new TextDecoder().decode(decrypted);

  // Zero sensitive intermediates
  zeroBytes(salt);
  zeroBytes(iv);
  zeroBytes(packed);
  zeroBytes(new Uint8Array(decrypted));

  return result;
}

// ─── URL key extraction ─────────────────────────────────────────────────────

// LINK FORMATS (URL structure, distinct from BLOB versions in cheque-keys.ts):
//   L1 = legacy:   /claim/{id}#{encryptedBlob}             (decrypt-only, ancient)
//   L2 = key:      /claim/{id}#k.{urlKey}[.{fundingToken}] (physical/QR)
//   L3 = redirect: /go/{id}?t={redirectToken}              (digital/messaging)
//
// Wire prefix `k.` replaces legacy `v2.`. Parser accepts BOTH so already-printed
// QR cheques (with `#v2.` baked in) keep working forever.
export interface FragmentL2 {
  version: 2;
  urlKey: string;
  fundingToken?: string;
}

export interface FragmentL1 {
  version: 1;
  encryptedBlob: string;
}

export type ParsedFragment = FragmentL2 | FragmentL1;

/** @deprecated use FragmentL2 */
export type FragmentV2 = FragmentL2;
/** @deprecated use FragmentL1 */
export type FragmentV1 = FragmentL1;

/**
 * Parse a URL fragment into L1 or L2 format.
 * L2: "k.{urlKey}"                 — 128-bit key only (new prefix)
 * L2: "k.{urlKey}.{fundingToken}"  — 128-bit key + funding token for Changelly auth
 * L2: "v2.{urlKey}[...]"           — legacy prefix, still parsed for printed cheques
 * L1: "{base64url(salt||iv||ct)}"  — full encrypted blob (legacy)
 */
export function parseFragment(fragment: string): ParsedFragment {
  // New prefix `k.` — used for all new claim URLs.
  if (fragment.startsWith("k.")) {
    const rest = fragment.slice(2);
    const dotIdx = rest.indexOf(".");
    if (dotIdx >= 0) {
      return { version: 2, urlKey: rest.slice(0, dotIdx), fundingToken: rest.slice(dotIdx + 1) };
    }
    return { version: 2, urlKey: rest };
  }
  // Legacy `v2.` prefix — printed QR cheques baked it into ink. Keep parsing forever.
  if (fragment.startsWith("v2.")) {
    const rest = fragment.slice(3);
    const dotIdx = rest.indexOf(".");
    if (dotIdx >= 0) {
      return { version: 2, urlKey: rest.slice(0, dotIdx), fundingToken: rest.slice(dotIdx + 1) };
    }
    return { version: 2, urlKey: rest };
  }
  return { version: 1, encryptedBlob: fragment };
}

/**
 * Extract the URL key from hash fragment.
 * L3 redirect-token proxy handles iOS in-app browsers (replaces deprecated ?k= path).
 *
 * Priority: #k. hash fragment → legacy #v2. fragment → L1 ancient blob
 */
export function extractUrlKey(): ParsedFragment | null {
  const raw = window.location.hash.slice(1);
  if (!raw) return null;
  // Strip any query-style "&pin=..." suffix (magic-link PIN); urlKey stays intact.
  const ampIdx = raw.indexOf("&");
  const fragment = ampIdx >= 0 ? raw.slice(0, ampIdx) : raw;
  if (!fragment) return null;
  return parseFragment(fragment);
}

/**
 * Clear sensitive key material from the URL bar (both query params and hash).
 * Uses history.replaceState to avoid triggering navigation.
 */
export function clearSensitiveUrl(): void {
  const params = new URLSearchParams(window.location.search);
  const hadKey = params.has("t") || params.has("pin") || !!window.location.hash;
  if (!hadKey) return;

  params.delete("t");
  params.delete("pin");
  const search = params.toString();
  const newUrl = window.location.pathname + (search ? `?${search}` : "");
  history.replaceState(null, "", newUrl);
}

/** @deprecated Use clearSensitiveUrl instead */
export function clearFragment(): void {
  clearSensitiveUrl();
}

// ─── Redirect Token (V3) ────────────────────────────────────────────────────
//
// V3 digital cheques use a redirect-token proxy to avoid exposing the urlKey
// in query params (which appear in server logs). Instead, an opaque redirect
// token is placed in the URL. The real urlKey is AES-encrypted with the token
// and stored server-side. Client-side JS decrypts it and redirects to the
// claim page with the urlKey in a hash fragment (never sent to the server).
//
// Physical cheques (QR codes) continue to use V2 hash fragments directly.
// ─────────────────────────────────────────────────────────────────────────────

/** Generate a cryptographically random 128-bit redirect token */
export function generateRedirectToken(): string {
  const token = crypto.getRandomValues(new Uint8Array(16));
  return uint8ArrayToBase64Url(token);
}

/** Derive an AES-256-GCM key from a redirect token + salt (100k iterations) */
async function deriveRedirectKey(token: string, salt: Uint8Array): Promise<CryptoKey> {
  const enc = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    enc.encode(token) as unknown as ArrayBuffer,
    "PBKDF2",
    false,
    ["deriveKey"]
  );
  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: salt as unknown as ArrayBuffer,
      iterations: 100_000,
      hash: "SHA-256",
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

/**
 * Encrypt a urlKey with a redirect token for server storage.
 * Returns packed base64url string: salt(16) + iv(12) + ciphertext+tag
 */
export async function encryptUrlKeyForRedirect(
  urlKey: string,
  redirectToken: string
): Promise<string> {
  const enc = new TextEncoder();
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveRedirectKey(redirectToken, salt);

  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    enc.encode(urlKey)
  );

  const ct = new Uint8Array(ciphertext);
  const packed = new Uint8Array(16 + 12 + ct.length);
  packed.set(salt, 0);
  packed.set(iv, 16);
  packed.set(ct, 28);

  const result = uint8ArrayToBase64Url(packed);
  zeroBytes(salt);
  zeroBytes(iv);
  zeroBytes(packed);
  return result;
}

/**
 * Decrypt a urlKey from a redirect_packed blob using the redirect token.
 * Returns null if decryption fails (wrong token).
 */
export async function decryptUrlKeyFromRedirect(
  redirectPacked: string,
  redirectToken: string
): Promise<string | null> {
  try {
    const packed = base64UrlToUint8Array(redirectPacked);
    const salt = packed.slice(0, 16);
    const iv = packed.slice(16, 28);
    const ciphertext = packed.slice(28);

    const key = await deriveRedirectKey(redirectToken, salt);
    const decrypted = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv },
      key,
      ciphertext
    );

    const result = new TextDecoder().decode(decrypted);
    zeroBytes(salt);
    zeroBytes(iv);
    zeroBytes(packed);
    zeroBytes(new Uint8Array(decrypted));
    return result;
  } catch {
    return null; // wrong token — AES-GCM auth tag mismatch
  }
}

// ─── Link building ───────────────────────────────────────────────────────────

/**
 * Build a cheque claim link.
 * L3: /go/{id}?t={redirectToken}          — digital (survives messaging apps, urlKey not in URL)
 * L2: /claim/{id}#k.{urlKey}              — physical (QR code, maximum security)
 * L1: /claim/{id}#{encryptedBlob}         — legacy
 *
 * `version` arg numbering preserved (1/2/3) for back-compat with existing callers;
 * semantically these are LINK formats L1/L2/L3, not blob versions.
 */
export function buildCheckLink(
  chequeId: string,
  payload: string,
  version: 1 | 2 | 3 = 3,
  options?: { fundingToken?: string; instance?: string }
): string {
  const origin = window.location.origin;

  if (version === 3) {
    const params = new URLSearchParams();
    params.set("t", payload);
    if (options?.fundingToken) params.set("ft", options.fundingToken);
    if (options?.instance) params.set("instance", options.instance);
    return `${origin}/go/${chequeId}?${params.toString()}`;
  }
  if (version === 2) {
    const query = options?.instance ? `?instance=${options.instance}` : "";
    const fundingSuffix = options?.fundingToken ? `.${options.fundingToken}` : "";
    return `${origin}/claim/${chequeId}${query}#k.${payload}${fundingSuffix}`;
  }
  // L1 legacy
  return `${origin}/claim/${chequeId}#${payload}`;
}

// ─── Memory safety ───────────────────────────────────────────────────────────

/** Zero a Uint8Array to remove sensitive data from memory */
export function zeroBytes(arr: Uint8Array): void {
  arr.fill(0);
}

// ─── Transaction verification ────────────────────────────────────────────────

const TOKEN_PROGRAM_ID = "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA";
const SYSTEM_PROGRAM_ID = "11111111111111111111111111111111";
const ASSOCIATED_TOKEN_PROGRAM_ID = "ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL";

const ALLOWED_PROGRAMS = new Set([
  TOKEN_PROGRAM_ID,
  SYSTEM_PROGRAM_ID,
  ASSOCIATED_TOKEN_PROGRAM_ID,
]);

/**
 * Verify a server-prepared transaction before signing.
 * Checks that:
 *  1. Only allowed Solana programs are invoked
 *  2. At least one USDC transfer exists TO the expected recipient
 *  3. Fee payer is the cheque wallet (not some attacker address)
 *  4. Cheque wallet is the source/authority on all transfers
 *
 * @param expectedRecipientAtas — set of valid destination token account addresses
 *        (e.g. recipient ATA, or recipient address itself if already a token account)
 */
export function verifyTransaction(
  transaction: { instructions: any[]; feePayer?: { toBase58(): string } },
  chequePublicAddress: string,
  expectedRecipientAtas?: Set<string>,
): void {
  // 1. Check all program IDs are in allow-list
  for (const ix of transaction.instructions) {
    const programId = ix.programId.toBase58();
    if (!ALLOWED_PROGRAMS.has(programId)) {
      throw new Error(
        `Transaction contains unauthorized program: ${programId}`
      );
    }
  }

  // 2. Find all TransferChecked instructions (opcode 12)
  //    Account layout: keys[0]=source, keys[1]=mint, keys[2]=destination, keys[3]=owner
  const transferIxs = transaction.instructions.filter(
    (ix: any) =>
      ix.programId.toBase58() === TOKEN_PROGRAM_ID &&
      ix.data.length > 0 &&
      ix.data[0] === 12
  );
  if (transferIxs.length === 0) {
    throw new Error("Transaction does not contain a USDC transfer instruction");
  }

  // 3. Verify cheque wallet is source/authority on ALL transfers
  for (const ix of transferIxs) {
    const source = ix.keys[0]?.pubkey?.toBase58();
    const owner = ix.keys[3]?.pubkey?.toBase58();
    if (owner !== chequePublicAddress) {
      throw new Error(
        `Transfer authority mismatch: expected ${chequePublicAddress}, got ${owner}`
      );
    }
  }

  // 4. Verify at least one transfer goes to expected recipient
  if (expectedRecipientAtas && expectedRecipientAtas.size > 0) {
    const hasRecipientTransfer = transferIxs.some((ix: any) => {
      const dest = ix.keys[2]?.pubkey?.toBase58();
      return dest && expectedRecipientAtas.has(dest);
    });
    if (!hasRecipientTransfer) {
      throw new Error("Transaction does not transfer funds to the expected recipient");
    }
  }

  // 5. Fee payer must be the cheque wallet
  if (transaction.feePayer && transaction.feePayer.toBase58() !== chequePublicAddress) {
    throw new Error(
      `Fee payer mismatch: expected ${chequePublicAddress}, got ${transaction.feePayer.toBase58()}`
    );
  }
}

/**
 * Verify a deferred V2 claim transaction before signing.
 *
 * Deferred transactions have TWO USDC transfers:
 *   [a] fundingATA → chequeATA      (authority = funding wallet, server-signed)
 *   [b] chequeATA → recipient       (authority = cheque wallet,  client-signed)
 * Plus a CloseAccount that cheque signs (rent → system wallet).
 * feePayer is the system wallet, not the cheque.
 *
 * Invariants (what we must verify to safely sign):
 *  1. Only whitelisted programs are invoked.
 *  2. At least one TransferChecked authored by the cheque exists.
 *  3. EVERY cheque-authored TransferChecked has destination in expectedRecipientAtas
 *     (no siphoning to attacker).
 *  4. Sum of cheque-authored TransferChecked amounts == expectedAmountMinor
 *     (no over-transfer to a decoy recipient).
 *
 * Fee payer is intentionally NOT constrained to the cheque (it's the system wallet).
 * Non-cheque-authored transfers (the funding leg) are permitted; they move funds
 * INTO the cheque wallet, not out of it, so they cannot harm the user.
 */
export function verifyDeferredTransaction(
  transaction: { instructions: any[]; feePayer?: { toBase58(): string } },
  chequePublicAddress: string,
  expectedRecipientAtas: Set<string>,
  expectedAmountMinor: bigint,
): void {
  // 1. All programs in allow-list
  for (const ix of transaction.instructions) {
    const programId = ix.programId.toBase58();
    if (!ALLOWED_PROGRAMS.has(programId)) {
      throw new Error(`Transaction contains unauthorized program: ${programId}`);
    }
  }

  // 2. Collect all TransferChecked (opcode 12) authored by the cheque wallet
  const transferIxs = transaction.instructions.filter(
    (ix: any) =>
      ix.programId.toBase58() === TOKEN_PROGRAM_ID &&
      ix.data.length > 0 &&
      ix.data[0] === 12
  );
  const chequeTransfers = transferIxs.filter(
    (ix: any) => ix.keys[3]?.pubkey?.toBase58() === chequePublicAddress
  );
  if (chequeTransfers.length === 0) {
    throw new Error("Deferred transaction has no cheque-authored transfer");
  }

  // 3. Every cheque-authored transfer must go to the expected recipient
  let totalAmount = 0n;
  for (const ix of chequeTransfers) {
    const dest = ix.keys[2]?.pubkey?.toBase58();
    if (!dest || !expectedRecipientAtas.has(dest)) {
      throw new Error(
        `Deferred cheque-authored transfer to unexpected destination: ${dest}`
      );
    }
    // Parse amount (little-endian u64 at offset 1)
    const dv = new DataView(ix.data.buffer, ix.data.byteOffset, ix.data.byteLength);
    totalAmount += dv.getBigUint64(1, true);
  }

  // 4. Total cheque-authored transfer amount must equal expected
  if (totalAmount !== expectedAmountMinor) {
    throw new Error(
      `Deferred transfer amount mismatch: expected ${expectedAmountMinor}, got ${totalAmount}`
    );
  }
}

// ─── Base64URL helpers ───────────────────────────────────────────────────────

export function uint8ArrayToBase64Url(arr: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < arr.length; i++) {
    binary += String.fromCharCode(arr[i]);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function base64UrlToUint8Array(str: string): Uint8Array {
  let base64 = str.replace(/-/g, "+").replace(/_/g, "/");
  while (base64.length % 4) base64 += "=";
  const binary = atob(base64);
  const arr = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    arr[i] = binary.charCodeAt(i);
  }
  return arr;
}

// ─── Config — Solana Mainnet ─────────────────────────────────────────────────

export const NETWORK_CONFIG = {
  name: "Solana",
  rpcUrl: "https://mainnet.helius-rpc.com",
  explorerUrl: "https://solscan.io",
  token: {
    symbol: "USDC",
    // USDC on Solana mainnet
    address: "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v",
    decimals: 6,
  },
  nativeCurrency: "SOL",
};

// TOKEN_MINTS has been removed. Use useTokenRegistry().bySymbol(symbol)?.mint
// inside React components, or getRegistryCached() / api.invoke("public/tokens")

// ─── OPRF V2 auto-dispatch ───────────────────────────────────────────────────
// Re-exported so callers can import from "@/lib/crypto" without reaching into shared/.
export { decryptPrivateKeyAuto } from "@shared/crypto/cheque-keys";
// for non-React call sites. See src/lib/useTokenRegistry.ts.
